import type { BlockSnapshot, SliceSnapshot } from '@blocksuite/affine/store';

/**
 * `Transformer.walk` only accepts a `DocSnapshot`, so walk slice snapshots
 * ourselves.
 */
export function walkBlockSnapshots(
  blocks: BlockSnapshot[],
  callback: (block: BlockSnapshot) => void
) {
  for (const block of blocks) {
    callback(block);
    if (block.children?.length) {
      walkBlockSnapshots(block.children, callback);
    }
  }
}

/**
 * Ids of the blobs (images, attachments) referenced by a slice snapshot. Blob
 * ids are content hashes, so they stay valid in any workspace we copy into.
 */
export function collectBlobIds(snapshot: SliceSnapshot): Set<string> {
  const blobIds = new Set<string>();
  walkBlockSnapshots(snapshot.content, block => {
    const sourceId = block.props?.sourceId as string | undefined;
    if (sourceId) {
      blobIds.add(sourceId.replace(/^\//, ''));
    }
  });
  return blobIds;
}

const LINKED_DOC_FLAVOURS = new Set([
  'affine:embed-linked-doc',
  'affine:embed-synced-doc',
]);

/**
 * Whether the snapshot references docs of the source workspace. Such references
 * cannot be resolved after the template is applied in another workspace, so the
 * user is warned at publish time.
 */
export function hasCrossDocReferences(snapshot: SliceSnapshot): boolean {
  let found = false;
  walkBlockSnapshots(snapshot.content, block => {
    if (found) return;
    if (LINKED_DOC_FLAVOURS.has(block.flavour)) {
      found = true;
      return;
    }
    // inline `LinkedPage` references live in the text delta
    const text = block.props?.text as
      | { delta?: { attributes?: Record<string, unknown> }[] }
      | undefined;
    if (
      text?.delta?.some(
        op =>
          !!(op.attributes as { reference?: unknown } | undefined)?.reference
      )
    ) {
      found = true;
    }
  });
  return found;
}

export function estimatePayloadSize(payload: unknown): number {
  try {
    return new TextEncoder().encode(JSON.stringify(payload)).byteLength;
  } catch {
    return 0;
  }
}
